import { NestedTableState } from '../../state';
import React, { useMemo } from 'react';
import {
  VirtualTableRepeater,
  VirtualTableRepeaterProps,
} from '../VirtualGridRepeater/VirtualTableRepeater';
import { VirtualGridStateProvider } from '../VirtualGridRepeater/VirtualGridStateContext';
import { NestedTableRow } from './NestedTableRow';
import { useNestedModeColumns } from './useNestedModeColumns';
import { useFlatModeColumns } from './useFlatModeColumns';
import { TableColumn as TableColumnWSR } from '@wix/design-system/dist/types/Table';

export interface UseModeTableProps<C extends string> {
  state: NestedTableState<C>;
  horizontalScroll?: boolean;
  actionsCellWidth?: string | number;
  actionsCellProps?: Partial<Omit<TableColumnWSR<any>, 'render' | 'style'>>;
}

export function useModeTableProps<C extends string>(
  props: UseModeTableProps<C>,
) {
  const {
    state: wrapperState,
    horizontalScroll,
    actionsCellWidth,
    actionsCellProps,
  } = props;

  const { isFlatMode } = wrapperState;
  const state = isFlatMode ? wrapperState.flat : wrapperState.nested;

  const key = wrapperState.isFlatMode.toString();

  const BodyElementType = useMemo(
    () =>
      ({ children }: VirtualTableRepeaterProps) =>
        (
          <VirtualGridStateProvider key={key} value={state.virtual}>
            <VirtualTableRepeater>{children}</VirtualTableRepeater>
          </VirtualGridStateProvider>
        ),
    [state, key],
  );

  const TrElementType = useMemo(
    () => (isFlatMode ? undefined : NestedTableRow),
    [isFlatMode],
  );

  const nestedColumns = useNestedModeColumns({
    state: wrapperState,
    horizontalScroll,
    actionsCellWidth,
    actionsCellProps,
  });

  const flatColumns = useFlatModeColumns({
    state: wrapperState,
    horizontalScroll,
    actionsCellWidth,
    actionsCellProps,
  });

  return {
    state,
    key,
    BodyElementType,
    TrElementType,
    columns: isFlatMode ? flatColumns : nestedColumns,
  };
}
